import React, { useEffect, useState } from 'react';

/**
 * UpdateBanner — "a new version is ready, update when YOU are ready".
 *
 * The PWA service worker keeps the app working offline, which means a newly
 * deployed version does NOT take over on its own (that could reload a cashier
 * mid-sale and lose the cart). Instead, index.js detects a new version WAITING
 * and fires `agribooks:update-ready`; this banner appears and the user updates
 * with one click whenever they're done (same pattern as Claude Code's
 * "relaunch to update"). Nothing reloads until the button is pressed.
 *
 * Mounted once in index.js beside <App/> so it covers POS, Sports and the
 * terminal WebView alike. Inline styles on purpose: it must render identically
 * on every surface, before any app CSS/theme loads.
 *
 * FALLBACK ONLY (owner 2026-07-17): inside the POS shell the notice lives in
 * the sidebar status area (OfflineIndicator's "New version ready" card, next
 * to Online / Ready-for-offline). While that card is mounted it CLAIMS the
 * notice (agribooks:update-ui-claimed / -released) and this banner hides;
 * surfaces with no sidebar (terminal WebView, Sports, login) still get it.
 */
export default function UpdateBanner() {
  const [reg, setReg] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [claimed, setClaimed] = useState(() => (window.__agribooksUpdateUIMounted || 0) > 0);

  useEffect(() => {
    // The update may have been detected before React mounted (SW checks run
    // at load) — index.js parks the registration on window for that race.
    if (window.__agribooksUpdateReady) setReg(window.__agribooksUpdateReady);
    const onReady = (e) => setReg(e.detail && e.detail.registration);
    const syncClaim = () => setClaimed((window.__agribooksUpdateUIMounted || 0) > 0);
    window.addEventListener('agribooks:update-ready', onReady);
    window.addEventListener('agribooks:update-ui-claimed', syncClaim);
    window.addEventListener('agribooks:update-ui-released', syncClaim);
    return () => {
      window.removeEventListener('agribooks:update-ready', onReady);
      window.removeEventListener('agribooks:update-ui-claimed', syncClaim);
      window.removeEventListener('agribooks:update-ui-released', syncClaim);
    };
  }, []);

  if (!reg || dismissed || claimed) return null;

  const applyUpdate = () => {
    setUpdating(true);
    try { window.__agribooksApplyUpdate(reg); }
    catch { window.location.reload(); }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2147483000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
      padding: '8px 14px', background: '#0f172a', color: '#e2e8f0',
      font: '13px/1.4 system-ui, sans-serif',
      boxShadow: '0 2px 10px rgba(0,0,0,.35)',
    }} data-testid="update-banner">
      <span style={{ minWidth: 0 }}>
        A new version of AgriBooks is ready.
        <span style={{ opacity: .75 }}> Finish what you're doing, then update — nothing is lost until you click.</span>
      </span>
      <button
        onClick={applyUpdate}
        disabled={updating}
        data-testid="update-banner-apply"
        style={{
          flexShrink: 0, border: 0, borderRadius: 6, cursor: 'pointer',
          padding: '6px 16px', fontWeight: 700, fontSize: 13,
          background: updating ? '#334155' : '#16a34a', color: '#fff',
        }}
      >
        {updating ? 'Updating…' : 'Update now'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss update notice"
        data-testid="update-banner-dismiss"
        style={{
          flexShrink: 0, border: 0, background: 'transparent', color: '#94a3b8',
          cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4,
        }}
      >
        ×
      </button>
    </div>
  );
}
